import type { ConversationDTO, ConversationMessagesDTO, PersistedChatMessageDTO } from '@shared/schemas/chat';
import type { QueryClient } from '@tanstack/react-query';
import { conversationKeys } from './useConversation';
import type { ChatUIMessage } from '../types';

function getMessageTextContent(message: ChatUIMessage) {
  return message.parts
    .filter((part): part is Extract<ChatUIMessage['parts'][number], { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
    .trim();
}

function toPersistedMessage(
  message: ChatUIMessage,
  conversationId: string,
  existing?: PersistedChatMessageDTO,
): PersistedChatMessageDTO {
  const textContent = getMessageTextContent(message);

  return {
    content: textContent || existing?.content || '',
    conversationId,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    id: message.id,
    metadata: existing?.metadata ?? null,
    parts: message.parts as PersistedChatMessageDTO['parts'],
    role: message.role,
  };
}

function buildFallbackConversation(
  conversationId: string,
  patch?: Partial<ConversationDTO>,
): ConversationDTO {
  const now = new Date().toISOString();

  return {
    createdAt: now,
    id: conversationId,
    lastMessageAt: now,
    lastMessagePreview: null,
    messageCount: 0,
    ragEnabled: false,
    title: null,
    updatedAt: now,
    webSearchEnabled: false,
    ...patch,
  };
}

export function syncConversationDetailCache(
  queryClient: QueryClient,
  conversationId: string,
  messages: ChatUIMessage[],
  conversationPatch?: Partial<ConversationDTO>,
) {
  queryClient.setQueryData<ConversationMessagesDTO>(
    conversationKeys.detail(conversationId),
    (current) => {
      const existingById = new Map(
        (current?.messages ?? []).map((message) => [message.id, message]),
      );
      const conversation = current?.conversation
        ? {
          ...current.conversation,
          ...conversationPatch,
          updatedAt: conversationPatch?.updatedAt ?? current.conversation.updatedAt,
        }
        : buildFallbackConversation(conversationId, conversationPatch);
      const lastMessage = messages.at(-1);

      return {
        conversation: {
          ...conversation,
          messageCount: messages.length,
          lastMessagePreview: lastMessage
            ? getMessageTextContent(lastMessage) || conversation.lastMessagePreview
            : conversation.lastMessagePreview,
          lastMessageAt: conversationPatch?.updatedAt ?? conversation.lastMessageAt,
        },
        messages: messages.map((message) => toPersistedMessage(
          message,
          conversationId,
          existingById.get(message.id),
        )),
      };
    },
  );
}

export function patchConversationTitleInCache(
  queryClient: QueryClient,
  conversationId: string,
  title: string | null,
) {
  queryClient.setQueryData<ConversationMessagesDTO>(
    conversationKeys.detail(conversationId),
    (current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        conversation: {
          ...current.conversation,
          title,
        },
      };
    },
  );
}

export function seedConversationDetailCache(
  queryClient: QueryClient,
  payload: {
    conversationId: string;
    ragEnabled: boolean;
    title: string | null;
    webSearchEnabled: boolean;
  },
) {
  queryClient.setQueryData<ConversationMessagesDTO>(
    conversationKeys.detail(payload.conversationId),
    (current) => ({
      conversation: current?.conversation
        ? {
          ...current.conversation,
          ragEnabled: payload.ragEnabled,
          title: payload.title,
          webSearchEnabled: payload.webSearchEnabled,
        }
        : buildFallbackConversation(payload.conversationId, {
          ragEnabled: payload.ragEnabled,
          title: payload.title,
          webSearchEnabled: payload.webSearchEnabled,
        }),
      messages: current?.messages ?? [],
    }),
  );
}
